import React from "react";
import styled from "styled-components";
import BodyBlocker from "./BodyBlocker";
import { breakpoints } from "../utils/styles";
import { useBreakpoints } from "../hooks/use-breakpoints";
import BackButton from "./common/BackButton";

const AsideBox = styled.aside<{ open?: boolean, largeTopPadding?: boolean }>`
    background: var(--page-bg);
    border-top-left-radius: 1.25rem;
    border-top-right-radius: 1.25rem;
    bottom: -100%;
    height: 95dvh;
    padding-top: ${(props) => (props.largeTopPadding ? "3rem" : "1.5rem")};
    position: fixed;
    top: auto;
    transition: 0.5s bottom ease-out;
    z-index: 1036;
    width: 100%;

    ${(props) => props.open && 'bottom: 0;'}

    @media screen and (min-width: ${breakpoints.desktop}px) {
        border-radius: 1.25rem;
        left: 50%;
        height: auto;
        max-height: 90vh;
        max-width: 80%;
        position: fixed;
        transform: translate(-50%,50%);
        width: 600px;
        z-index: 1050;

        ${(props) => props.open && 'bottom: 50%;'}
    }
`;

const CloseCross = styled.button`
    cursor: pointer;
    position: absolute;
    right: 1rem;
    top: 0.5rem;
    background: none;
    border: none;
    outline: none;
    font-size: 1.5em;
    color: var(--text-color);
    z-index: 1;
    &:hover {
        color: var(--primary-b);
    }
    &:focus {
        outline: none;
    }
`;

const BlockerContainer = styled.div<Pick<Props, "open">>`
    transition: opacity 0.5s ease;
    opacity: ${(props) => (props.open ? "1" : "0")};
    display: ${(props) => (props.open ? "block" : "none")};
`;

const PanelTitle = styled.h3`
    color: var(--primary-b) !important;
    font-family: var(--ff-title);
    font-size: 3.9rem !important;
    font-weight: 500;
    line-height: 1;
    text-align: left;
    text-transform: uppercase;
`;

const PanelBody = styled.div`
    max-height: calc(90vh - 3rem);
    overflow-y: auto;
    padding: 0 var(--pad-x) 1rem;
    position: relative;
    text-align: center;
`;

const PanelContent = styled.div`
    padding-bottom: 1rem;
`;

const InlineTitle = styled.div`
    font-family: var(--ff-title);
    font-size: 3.9rem !important;
    font-weight: 500;
    line-height: 1;
    text-align: left;
    text-transform: uppercase;

    @media screen and (min-width: ${breakpoints.desktop}px) {
        font-size: 2.5rem !important;
    }
`;

type Props = React.PropsWithChildren<{
    blockBg?: boolean;
    desktopInline?: boolean;
    open?: boolean;
    title?: string | React.ReactNode;
    onClose?: () => void;
}>;

const BottomPanel: React.FC<Props> = ({ children, desktopInline = false, title, ...props }) => {
    const { desktop: isDesktop } = useBreakpoints();
    const [open, setOpen] = React.useState(false);

    React.useEffect(() => {
        setTimeout(() => {
            setOpen(props.open || false);
        }, 100);
    }, [props.open]);

    function onClose() {
        setOpen(false);
        setTimeout(() => {
            props.onClose && props.onClose();
        }, isDesktop && desktopInline ? 1 : 400);
    }

    if (isDesktop && desktopInline) {
        return <>
            {props.onClose && <BackButton
                onBackClick={onClose}
                onLabelClick={onClose}
                className="mb-3"
            />}
            <InlineTitle className="mb-5">{title}</InlineTitle>
            {children}
        </>;
    }

    return (
        <>
            {open && props.blockBg !== false && (
                <BlockerContainer open={open}>
                    <BodyBlocker />
                </BlockerContainer>
            )}
            <AsideBox className="l-wrapper__sidebar-over" open={open} largeTopPadding={!!props.onClose}>
                {props.onClose && <CloseCross onClick={onClose}>
                    <i className="fal fa-times"></i>
                </CloseCross>}
                <PanelBody>
                    <div>
                        {title && <PanelTitle>{title}</PanelTitle>}
                        <PanelContent>{children}</PanelContent>
                    </div>
                </PanelBody>
            </AsideBox>
        </>
    );
};

export default BottomPanel;
